/**
 * On-site interruption policy — the one place that says how often a visitor
 * may be interrupted on a business's website, and where never.
 *
 * Every popup used to decide on its own whether to show. Nothing owned the
 * visitor's experience across campaigns, and we watched what that does on our
 * own site: two popups in a row on the first page, a popup on the sign-up
 * page, another on the MCP consent screen. Each campaign was fine alone.
 *
 * The SDK enforces this at show time (it is the only place that knows the page
 * the visitor is on right now), the init route sends it, and the builder and
 * the agent read it from here. A number written anywhere else is a second copy
 * that will drift.
 *
 * No imports, on purpose: the browser loader bundles this file.
 */

export const ONSITE_POLICY = {
  /** At most this many popups on one page view. */
  popupsPerPageView: 1,
  /**
   * At most this many on-site messages (popups and notifications) in one
   * browser session. A business can change it in its connection settings.
   */
  maxOnSiteMessagesPerSession: 2,
  /** After a popup shows, no other popup for this long. */
  popupCooldownMs: 3 * 60 * 1000,
  /**
   * A visitor who closes a popup has said no to it: that popup rests for them
   * this long, across visits, on that browser.
   */
  restAfterCloseMs: 24 * 60 * 60 * 1000,
  /** Closed this many times, a popup stops for that visitor for `stopAfterClosesMs`. */
  closesBeforeStop: 3,
  stopAfterClosesMs: 30 * 24 * 60 * 60 * 1000,
} as const;

/**
 * Why a popup was held back for a visitor who closed it: within a day of the
 * last close, or after closing it `closesBeforeStop` times.
 */
export const CLOSE_REST_REASONS = ['closed_recently', 'closed_too_often'] as const;
export type CloseRestReason = (typeof CLOSE_REST_REASONS)[number];

/**
 * The policy as one sentence, for every prompt, skill and tool description
 * that tells the agent how often a visitor may be interrupted.
 *
 * Five pieces of guidance once typed these numbers by hand, and they
 * disagreed: "once per session", "max 2 per visit", "max 3 notifications per
 * session", "24-48 hours after dismissal". Only one of those was enforced.
 * Prose reads the numbers from here, so it cannot promise what the SDK does
 * not do.
 */
export function onsitePolicyInWords(): string {
  const { popupsPerPageView, maxOnSiteMessagesPerSession, popupCooldownMs } = ONSITE_POLICY;
  const { restAfterCloseMs, closesBeforeStop, stopAfterClosesMs } = ONSITE_POLICY;
  const HOUR = 60 * 60 * 1000;
  return (
    `A visitor sees at most ${popupsPerPageView} popup per page view and ` +
    `${maxOnSiteMessagesPerSession} on-site messages per visit (popups and notifications together; ` +
    `a business can change the per-visit number), with ${popupCooldownMs / 60_000} minutes between popups. ` +
    `A popup the visitor closes does not show to them again for ${restAfterCloseMs / HOUR} hours, ` +
    `and after ${closesBeforeStop} closes not for ${stopAfterClosesMs / (24 * HOUR)} days. ` +
    'Bars count as popups. Sign-in, sign-up, password, consent, checkout and payment pages never show one ' +
    'unless the business allows it for that campaign.'
  );
}

/**
 * Why a page is protected. A popup on one of these pages stands between a
 * person and the thing they came to finish.
 */
export type ProtectedPageReason =
  | 'sign_in'
  | 'sign_up'
  | 'password'
  | 'consent'
  | 'checkout'
  | 'payment'
  | 'opted_out';

/**
 * The protected reasons a campaign may opt back into, in the order the builder
 * lists them. `opted_out` is not here: it is the SITE saying never, and one
 * campaign does not outrank the page it runs on.
 */
export const OVERRIDABLE_PROTECTED_REASONS = [
  'sign_in',
  'sign_up',
  'password',
  'consent',
  'checkout',
  'payment',
] as const satisfies readonly ProtectedPageReason[];

export type OverridableProtectedReason = (typeof OVERRIDABLE_PROTECTED_REASONS)[number];

/**
 * The protected pages a campaign chose to show on anyway, from its
 * `targeting.rules.protectedPages` rule: `{ enabled, allow: [...] }`.
 *
 * Absent, disabled or malformed means none — every protected page stays
 * protected, which is what every campaign written before the rule existed
 * gets. Unknown reasons are dropped, so a stored typo cannot open a page.
 */
export function allowedProtectedReasons(rule: unknown): OverridableProtectedReason[] {
  if (!rule || typeof rule !== 'object') return [];
  const { enabled, allow } = rule as { enabled?: unknown; allow?: unknown };
  if (enabled === false || !Array.isArray(allow)) return [];
  const known = new Set<string>(OVERRIDABLE_PROTECTED_REASONS);
  return OVERRIDABLE_PROTECTED_REASONS.filter((reason) => allow.includes(reason) && known.has(reason));
}

/**
 * Words that mark a protected page when they are a whole URL path segment, or
 * a whole word inside one (`/en/sign-in`, `/oauth/consent`, `/checkouts/c/1`).
 * Whole words only: `auth` must not match `/authors`.
 */
const PROTECTED_WORDS: Readonly<Record<string, ProtectedPageReason>> = {
  signin: 'sign_in',
  login: 'sign_in',
  logon: 'sign_in',
  auth: 'sign_in',
  sso: 'sign_in',
  mfa: 'sign_in',
  '2fa': 'sign_in',
  otp: 'sign_in',
  signup: 'sign_up',
  register: 'sign_up',
  registration: 'sign_up',
  password: 'password',
  oauth: 'consent',
  oauth2: 'consent',
  consent: 'consent',
  authorize: 'consent',
  checkout: 'checkout',
  checkouts: 'checkout',
  payment: 'payment',
  payments: 'payment',
};

/** Two-word forms, written `sign-in`, `sign_in` or `sign.in` in a URL. */
const PROTECTED_PAIRS: Readonly<Record<string, ProtectedPageReason>> = {
  'sign in': 'sign_in',
  'log in': 'sign_in',
  'sign up': 'sign_up',
  'reset password': 'password',
  'forgot password': 'password',
};

/**
 * The protected reason for a URL path, or null.
 *
 * Takes a pathname (`/auth/signin`) or a full URL; only the path is read, so a
 * query string like `?next=/checkout` does not protect a product page.
 */
export function protectedPathReason(pathOrUrl: string): ProtectedPageReason | null {
  let path = pathOrUrl;
  try {
    path = new URL(pathOrUrl, 'https://x.invalid').pathname;
  } catch {
    // Not parseable as a URL — read it as a bare path.
  }
  let decoded = path;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    // Malformed escape: the raw path is still worth checking.
  }
  for (const segment of decoded.toLowerCase().split('/')) {
    if (!segment) continue;
    const words = segment.split(/[-_.]+/).filter(Boolean);
    for (let i = 0; i < words.length; i++) {
      const word = words[i]!;
      const single = PROTECTED_WORDS[word];
      if (single) return single;
      const next = words[i + 1];
      if (next) {
        const pair = PROTECTED_PAIRS[`${word} ${next}`];
        if (pair) return pair;
      }
    }
  }
  return null;
}

/**
 * True when a campaign flow (its `orchestration.flow.nodes`) has an on-site
 * popup step anywhere in it. Read by the builder's conflict note and by the
 * live-popup list, so both agree on what counts as a popup campaign.
 */
export function flowHasPopupStep(nodes: unknown): boolean {
  if (!Array.isArray(nodes)) return false;
  return nodes.some(
    (node) =>
      (node as { type?: unknown } | null)?.type === 'tool' &&
      (node as { data?: { toolType?: unknown } } | null)?.data?.toolType === 'onsite/popup',
  );
}

/**
 * A business marks a page as never-interrupt with this meta tag, for pages a
 * URL cannot describe (a sign-up at `/get-started`, a booking form):
 *
 *   <meta name="hamla:popups" content="off">
 */
export const ONSITE_OPT_OUT_META = 'hamla:popups';
